// Phase 12 — "My symbols" panel. Renders favorites, recently viewed, and
// recently copied lists from the personal library store. Used both on the
// dedicated /my-symbols route and as a homepage section when populated.

import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  clearFavorites,
  clearLibrary,
  clearRecentlyCopied,
  clearRecentlyViewed,
  clearSnippets,
  toggleFavorite,
  toggleSnippet,
  useLibrary,
  type RecentEntry,
} from "@/lib/library";
import { getSymbolName } from "@/data/symbol-names";
import { SYMBOL_SLUG_MAP } from "@/data/symbol-name-map.generated";
import { copyText } from "@/lib/copy";

interface Props {
  /** Hide entirely when all lists are empty (used on the homepage). */
  hideWhenEmpty?: boolean;
  /** Compact heading for embedded contexts. */
  compact?: boolean;
}

function slugFor(symbol: string): string | null {
  // Light slug map (not the full index) — null means the symbol has no detail page.
  return SYMBOL_SLUG_MAP[symbol] ?? null;
}

function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

export function MySymbolsPanel({ hideWhenEmpty, compact }: Props) {
  const lib = useLibrary();
  const [mounted, setMounted] = useState(false);
  // Avoid SSR/CSR hydration mismatch — server snapshot is always empty.
  useEffect(() => setMounted(true), []);

  if (!mounted) return null;

  const isEmpty =
    lib.favorites.length === 0 &&
    lib.savedSnippets.length === 0 &&
    lib.recentlyViewed.length === 0 &&
    lib.recentlyCopied.length === 0;

  if (hideWhenEmpty && isEmpty) return null;

  return (
    <section aria-labelledby="my-symbols-heading" className="w-full">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2
            id="my-symbols-heading"
            className={
              compact
                ? "font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground"
                : "font-display text-2xl font-bold sm:text-3xl"
            }
          >
            My symbols
          </h2>
          {!compact && (
            <p className="mt-1 text-sm text-muted-foreground">
              Saved on this device only — nothing leaves your browser.
            </p>
          )}
        </div>
        {!isEmpty && (
          <button
            type="button"
            onClick={() => {
              if (
                typeof window !== "undefined" &&
                window.confirm("Clear all favorites and history on this device?")
              ) {
                clearLibrary();
              }
            }}
            className="rounded-md border border-border bg-surface/60 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
          >
            Clear everything
          </button>
        )}
      </div>

      {isEmpty ? (
        <div className="glass rounded-2xl p-6 text-sm text-muted-foreground">
          <p>
            Star a symbol with ☆, save an example snippet from a use-case page, or
            open any symbol page to start your library. Everything is kept locally —
            no account required.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <FavoritesList items={lib.favorites} />
            <RecentList
              title="Recently viewed"
              items={lib.recentlyViewed}
              emptyHint="Pages you open will show up here."
              onClear={clearRecentlyViewed}
            />
            <RecentList
              title="Recently copied"
              items={lib.recentlyCopied}
              emptyHint="Symbols you copy will show up here."
              onClear={clearRecentlyCopied}
            />
          </div>
          {lib.savedSnippets.length > 0 && (
            <div className="mt-4">
              <SnippetsList items={lib.savedSnippets} />
            </div>
          )}
        </>
      )}
    </section>
  );
}

function FavoritesList({ items }: { items: string[] }) {
  return (
    <div className="glass min-w-0 rounded-2xl p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Favorites <span className="text-foreground">({items.length})</span>
        </h3>
        {items.length > 0 && (
          <button
            type="button"
            onClick={() => clearFavorites()}
            className="text-xs text-muted-foreground hover:text-destructive"
          >
            Clear
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Tap the ☆ on any symbol page to save it here.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {items.map((s) => {
            const slug = slugFor(s);
            const name = getSymbolName(s);
            const tile = (
              <span
                aria-hidden="true"
                className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-surface/60 text-xl group-hover:border-primary/50"
              >
                {s}
              </span>
            );
            return (
              <li key={s} className="group relative">
                {slug ? (
                  <Link
                    to="/symbol/$slug"
                    params={{ slug }}
                    aria-label={`Open ${name}`}
                    title={name}
                    className="block"
                  >
                    {tile}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => copyText(s, { source: "symbol-tile", symbol: s })}
                    aria-label={`Copy ${name}`}
                    title={name}
                  >
                    {tile}
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleFavorite(s);
                  }}
                  aria-label={`Unfavorite ${name}`}
                  className="absolute -right-1 -top-1 hidden h-5 w-5 items-center justify-center rounded-full border border-border bg-background text-[10px] text-muted-foreground hover:text-destructive group-hover:flex"
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SnippetsList({ items }: { items: string[] }) {
  const [copied, setCopied] = useState<string | null>(null);
  async function copy(text: string) {
    const ok = await copyText(text, { source: "use-case-example" });
    if (ok) {
      setCopied(text);
      window.setTimeout(() => setCopied((c) => (c === text ? null : c)), 1200);
    }
  }
  return (
    <div className="glass min-w-0 rounded-2xl p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Saved snippets <span className="text-foreground">({items.length})</span>
        </h3>
        <button
          type="button"
          onClick={() => clearSnippets()}
          className="text-xs text-muted-foreground hover:text-destructive"
        >
          Clear
        </button>
      </div>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((t) => {
          const plain = t.replace(/\n/g, " ");
          return (
            <li key={t} className="relative">
              <button
                type="button"
                onClick={() => copy(t)}
                aria-label={`Copy snippet: ${plain}`}
                className="flex w-full flex-col gap-1 rounded-lg border border-border bg-surface/60 px-3 py-2 pr-9 text-left transition-colors hover:border-primary/40"
              >
                <span className="whitespace-pre-wrap break-words font-sans text-base leading-snug text-foreground">
                  {t}
                </span>
                {copied === t && <span className="text-[10px] text-accent">Copied!</span>}
              </button>
              <button
                type="button"
                onClick={() => toggleSnippet(t)}
                aria-label={`Remove "${plain}" from saved snippets`}
                title="Remove"
                className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-[11px] text-muted-foreground hover:text-destructive"
              >
                ✕
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RecentList({
  title,
  items,
  emptyHint,
  onClear,
}: {
  title: string;
  items: RecentEntry[];
  emptyHint: string;
  onClear: () => void;
}) {
  return (
    <div className="glass min-w-0 rounded-2xl p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {title} <span className="text-foreground">({items.length})</span>
        </h3>
        {items.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-muted-foreground hover:text-destructive"
          >
            Clear
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyHint}</p>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 12).map((entry) => {
            const slug = slugFor(entry.symbol);
            const name = getSymbolName(entry.symbol);
            return (
              <li key={`${entry.symbol}-${entry.at}`}>
                {slug ? (
                  <Link
                    to="/symbol/$slug"
                    params={{ slug }}
                    className="flex items-center gap-2 rounded-md border border-transparent px-1.5 py-1 hover:border-border hover:bg-surface/60"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-8 w-8 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-lg"
                    >
                      {entry.symbol}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs">{name}</span>
                    <span className="flex-none text-[10px] uppercase tracking-wider text-muted-foreground">
                      {timeAgo(entry.at)}
                    </span>
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => copyText(entry.symbol, { source: "symbol-tile", symbol: entry.symbol })}
                    className="flex w-full items-center gap-2 rounded-md border border-transparent px-1.5 py-1 hover:border-border hover:bg-surface/60"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-8 w-8 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-lg"
                    >
                      {entry.symbol}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs">{name}</span>
                    <span className="flex-none text-[10px] uppercase tracking-wider text-muted-foreground">
                      {timeAgo(entry.at)}
                    </span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
